/**
 * Themed confirm/prompt dialogs — replacement for browser-native
 * ``window.confirm`` / ``window.prompt`` which look completely
 * out-of-place next to the Ponte Pos Shadcn/Tailwind UI.
 *
 * How it works
 * ------------
 * The <ConfirmDialogHost /> component (mounted once at the App root)
 * calls ``_registerHandler`` to hook itself into this module. Any
 * caller then simply does::
 *
 *     import { confirmDialog, promptDialog } from "@/lib/confirmDialog";
 *
 *     if (!(await confirmDialog("Διαγραφή παραγγελίας #123;"))) return;
 *
 *     const code = await promptDialog({
 *       title: "Κωδικός διαχειριστή",
 *       inputType: "password",
 *     });
 *
 * The returned Promise resolves to:
 *   • ``true`` / ``false`` for confirm
 *   • the string value / ``null`` for prompt (null == cancelled)
 *
 * If the host is not yet mounted (edge case during boot) we fall
 * back to the browser primitives so nothing crashes.
 */

let _handler = null;

export function _registerConfirmHandler(fn) {
  _handler = fn;
  return () => { if (_handler === fn) _handler = null; };
}

function _normalizeConfirm(input) {
  if (typeof input === "string") return { message: input };
  return input || {};
}

function _normalizePrompt(input, defaultVal) {
  if (typeof input === "string") return { message: input, defaultValue: defaultVal ?? "" };
  return { defaultValue: defaultVal ?? "", ...(input || {}) };
}

export function confirmDialog(input) {
  const opts = _normalizeConfirm(input);
  if (!_handler) {
    // Fallback for the boot window before <ConfirmDialogHost /> mounts.
    return Promise.resolve(
      // eslint-disable-next-line no-alert
      typeof window !== "undefined" ? window.confirm(opts.message || "") : false,
    );
  }
  return new Promise((resolve) => {
    _handler({ kind: "confirm", opts, resolve });
  });
}

export function promptDialog(input, defaultVal) {
  const opts = _normalizePrompt(input, defaultVal);
  if (!_handler) {
    return Promise.resolve(
      typeof window !== "undefined"
        // eslint-disable-next-line no-alert
        ? window.prompt(opts.message || "", opts.defaultValue || "")
        : null,
    );
  }
  return new Promise((resolve) => {
    _handler({ kind: "prompt", opts, resolve });
  });
}
